import { gql, useApolloClient, useSubscription } from '@apollo/client';
import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import { WorkItemStatus } from '../__generated__/globalTypes';
import { evictWorkItemCounts, evictWorkItemReads, syncTransactionWorkItemChips } from '../providers/apolloCache';

import { useFeatureGate } from './useFeatureGates';
import { useWsReconnectEffect } from './useWsReconnectEffect';

import type {
  WorkItemUpdatedSubscription,
  WorkItemUpdatedSubscriptionVariables,
} from './__generated__/useWorkItemLiveUpdates.gql';

/**
 * The card fields land on the normalized `WorkItem`, so an open Center updates
 * copy and badges in place; membership and counts ride the evictions below,
 * which a payload cannot express. Two selections earn their place beyond that:
 * `status`, the membership discriminator, derived per subscriber on the server
 * exactly as the list queries derive it; and `transactions { id }`, the item's
 * current links, which the chip sync writes onto each `Transaction`. No other
 * relation is selected — the rest is re-read rather than pushed.
 */
const WORK_ITEM_LIVE_FIELDS = gql`
  fragment WorkItemLiveFields on WorkItem {
    id
    title
    body
    summary
    category
    read
    status
    dueDate
    createdAt
    updatedAt
    transactions {
      id
    }
  }
`;

const WORK_ITEM_UPDATED_SUBSCRIPTION = gql`
  subscription WorkItemUpdated {
    workItemUpdated {
      ...WorkItemLiveFields
    }
  }
  ${WORK_ITEM_LIVE_FIELDS}
`;

/**
 * How long to hold evictions after a delivery so a producer burst (a sweep
 * archiving several items, a bulk mark-read) coalesces into one refetch wave
 * instead of one per event.
 */
const PUSH_COALESCE_MS = 750;

interface IPendingWorkItemEvent {
  id: string;
  status: WorkItemStatus;
  read: boolean;
  transactionIds: string[];
}

/**
 * Live work-item ("alerts") updates for the whole app — mount once in the app
 * shell, inside the authenticated tree, beside `useFinancialAccountLiveUpdates`.
 *
 * An entity push cannot add or remove list members or move aggregate counts,
 * so a delivery is treated as a doorbell: the pushed scalars land on the
 * normalized `WorkItem`, and this hook evicts the work-item reads through the
 * same centralized seam the mutation handlers use. Which reads depends on
 * what the delivery implies. Both lists partition on `archived` alone, so only
 * a crossing of that line moves a row between them and evicts list + counts;
 * an unseen *unread* id is treated the same way, conservatively, since it may
 * be new. Everything else evicts counts only — a read flip, a copy edit, or a
 * PENDING/ACTIVE move, which re-sections the Center from the normalized entity
 * without changing membership. Evicting the list collapses the Center's loaded
 * pages, and the most frequent event on this topic is the viewer's own
 * mark-read echoing back — usually on a row that predates the socket, which is
 * why "unseen but already read" cannot be treated as new.
 *
 * Unlike `useFinancialAccountLiveUpdates`, this hook owns no reconciliation
 * query: the sidebar's counts poll is this surface's permanent floor (the
 * chrome never unmounts), and evicted reads refetch on their own next render.
 * Delivery is at-most-once with no replay, so a ws reconnect evicts both
 * reads outright and forgets seen statuses — events lost in the gap can
 * never be classified, and an unseen-again id must over-evict, not under.
 *
 * @returns nothing; the hook exists for its subscription and cache effects.
 */
export const useWorkItemLiveUpdates = (): void => {
  const subscriptionsEnabled = useFeatureGate(FeatureGate.GraphqlSubscriptions);
  // The same gate the bell and the Center read: it decides whether the alert
  // surfaces exist at all, so this hook must go dark with them.
  const alertsEnabled = useFeatureGate(FeatureGate.AlertsCenter);
  const enabled = subscriptionsEnabled && alertsEnabled;

  const client = useApolloClient();
  const lastStatusByIdRef = React.useRef(new Map<string, WorkItemStatus>());
  const pendingRef = React.useRef<IPendingWorkItemEvent[]>([]);
  const flushTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushPending = React.useCallback(() => {
    flushTimerRef.current = null;
    const batch = pendingRef.current;
    pendingRef.current = [];
    if (batch.length === 0) {
      return;
    }
    const lastStatusById = lastStatusByIdRef.current;
    // Both lists partition on `archived` alone, so only a crossing of that line
    // moves a row between them. A PENDING/ACTIVE change re-sections the Center
    // and moves counts, but the pushed `status` lands on the normalized entity
    // and re-renders it in place — evicting the list for that would collapse
    // loaded pages to page one for a row that never left the list.
    const membershipChanged = batch.some((event) => {
      const last = lastStatusById.get(event.id);
      if (last !== undefined) {
        return (last === WorkItemStatus.Archived) !== (event.status === WorkItemStatus.Archived);
      }
      // First delivery for this id on this socket — which is what the rows a
      // user clicks look like, since they usually predate the socket. An
      // already-read row cannot be a new item (creation starts unread), so
      // unless it arrives archived this is an existing row's echo, not a
      // membership change; a wrong guess here only delays the list until the
      // Center's counts-transition resync or the poll floor.
      return !event.read || event.status === WorkItemStatus.Archived;
    });
    for (const event of batch) {
      lastStatusById.set(event.id, event.status);
      // Mirror the mutation path's chip treatment so an archive arriving from
      // another session clears `Transaction.workItem` the same way a local
      // archive does — the evictions below never reach transaction reads.
      syncTransactionWorkItemChips(client.cache, {
        id: event.id,
        archived: event.status === WorkItemStatus.Archived,
        transactionIds: event.transactionIds,
      });
    }
    if (membershipChanged) {
      evictWorkItemReads(client.cache);
    } else {
      evictWorkItemCounts(client.cache);
    }
  }, [client]);

  useSubscription<WorkItemUpdatedSubscription, WorkItemUpdatedSubscriptionVariables>(WORK_ITEM_UPDATED_SUBSCRIPTION, {
    skip: !enabled,
    onData: ({ data }) => {
      const workItem = data.data?.workItemUpdated;
      if (!workItem) {
        return;
      }
      pendingRef.current.push({
        id: workItem.id,
        status: workItem.status,
        read: workItem.read,
        transactionIds: workItem.transactions.map((transaction) => transaction.id),
      });
      flushTimerRef.current ??= setTimeout(flushPending, PUSH_COALESCE_MS);
    },
    onError: (error) => {
      logger.warn(`[work-item-live] subscription error: ${String(error)}`);
    },
  });

  // Clear a scheduled flush and its batch when the hook unmounts or `enabled`
  // flips; the dropped batch is exactly what the poll floor exists to
  // reconcile, and a gate turned off mid-session must not evict afterward.
  React.useEffect(() => {
    return () => {
      if (flushTimerRef.current !== null) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      pendingRef.current = [];
    };
  }, [enabled]);

  useWsReconnectEffect(enabled, () => {
    // Drop any straggler batch along with the map: a flush firing after the
    // forget would re-seed pre-gap statuses, and a post-gap delivery matching
    // one would then read as "no change" across states this hook never saw.
    if (flushTimerRef.current !== null) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    pendingRef.current = [];
    lastStatusByIdRef.current.clear();
    evictWorkItemReads(client.cache);
  });
};
